import React, { useEffect, useMemo, useState } from "react";
import { supabase } from "../supabase";
import { useAuth } from "../context/AuthContext";
import toast from "react-hot-toast";
import {
  RefreshCcw,
  ShieldCheck,
  Search,
  Plus,
  Check,
  X,
  Lock,
  Unlock,
  FolderKey,
  Layers,
  History,
} from "lucide-react";
import { ALL_MODULES } from "../utils/organizationModules";
import { MODULE_ORG_ALIAS_MAP } from "../utils/accessModules";

const RolesConfig = ({ onNavigate }) => {
  const { profile, user } = useAuth();
  const canManageUsers = ["admin", "administrador"].includes(String(profile?.role || "").toLowerCase());
  const isSuperAdmin = String(user?.email || "").toLowerCase() === "adpenia@gmail.com";

  const [loading, setLoading] = useState(true);
  const [roles, setRoles] = useState(["cajero", "vendedor", "operario", "encargado", "admin", "administrador"]);
  const [selectedRole, setSelectedRole] = useState("cajero");
  const [permissions, setPermissions] = useState([]);
  const [isAdding, setIsAdding] = useState(false);
  const [newRoleName, setNewRoleName] = useState("");
  const [busquedaModulo, setBusquedaModulo] = useState("");
  const [categoria, setCategoria] = useState("todas");
  const [orgModules, setOrgModules] = useState(null);

  const availableModules = useMemo(() => {
    const orgModulesSet = Array.isArray(orgModules) ? new Set(orgModules) : null;

    return ALL_MODULES.filter((moduleRow) => {
      if (!orgModulesSet) return true;
      const candidateIds = [moduleRow.id, ...(MODULE_ORG_ALIAS_MAP[moduleRow.id] || [])];
      return candidateIds.some((candidateId) => orgModulesSet.has(candidateId));
    });
  }, [orgModules]);

  const categories = useMemo(() => {
    const cats = [...new Set(availableModules.map((moduleRow) => moduleRow.category).filter(Boolean))];
    return ["todas", ...cats];
  }, [availableModules]);

  useEffect(() => {
    if (!profile?.organization_id) return;
    fetchRoleTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.organization_id]);

  const fetchRoleTemplates = async () => {
    try {
      setLoading(true);
      const [{ data, error }, { data: orgData, error: orgError }] = await Promise.all([
        supabase
          .from("role_templates")
          .select("*")
          .eq("organization_id", profile.organization_id),
        supabase
          .from("organizations")
          .select("custom_modules")
          .eq("id", profile.organization_id)
          .single(),
      ]);

      if (error) throw error;
      if (orgError) throw orgError;

      setPermissions(data || []);
      setOrgModules(Array.isArray(orgData?.custom_modules) ? orgData.custom_modules : null);

      const dbRoles = [...new Set((data || []).map((item) => item.role_name))].filter(Boolean);
      setRoles((prev) => [...new Set([...prev, ...dbRoles])]);

      const nextRoles = [...new Set([...roles, ...dbRoles])];
      if (nextRoles.length && !nextRoles.includes(selectedRole)) {
        setSelectedRole(nextRoles[0]);
      }
    } catch (error) {
      console.error(error);
      toast.error("Error al cargar plantillas");
      setPermissions([]);
    } finally {
      setLoading(false);
    }
  };

  const isChecked = (moduleId) =>
    permissions.some((permissionRow) => permissionRow.role_name === selectedRole && permissionRow.module_id === moduleId);

  const togglePermission = async (moduleId) => {
    if (!canManageUsers) return;

    const exists = isChecked(moduleId);

    try {
      if (exists) {
        setPermissions((prev) =>
          prev.filter((permissionRow) => !(permissionRow.role_name === selectedRole && permissionRow.module_id === moduleId))
        );

        const { error } = await supabase
          .from("role_templates")
          .delete()
          .eq("organization_id", profile.organization_id)
          .eq("role_name", selectedRole)
          .eq("module_id", moduleId);

        if (error) throw error;
      } else {
        const newPermission = {
          organization_id: profile.organization_id,
          role_name: selectedRole,
          module_id: moduleId,
        };

        setPermissions((prev) => [...prev, newPermission]);

        const { error } = await supabase.from("role_templates").insert([newPermission]);
        if (error) throw error;
      }

      toast.success("Sincronizado");
    } catch (error) {
      console.error(error);
      toast.error("Error de red");
      fetchRoleTemplates();
    }
  };

  const confirmAddRole = () => {
    if (!canManageUsers) return;

    const roleName = newRoleName.trim().toLowerCase();
    if (!roleName) return toast.error("Nombre invalido");
    if (roles.includes(roleName)) return toast.error("El rol ya existe");

    setRoles((prev) => [...prev, roleName]);
    setSelectedRole(roleName);
    setNewRoleName("");
    setIsAdding(false);
    toast.success("Rol creado. Asignale modulos para guardarlo.");
  };

  const filteredModules = useMemo(() => {
    const query = busquedaModulo.trim().toLowerCase();

    return availableModules
      .filter((moduleRow) => (categoria === "todas" ? true : moduleRow.category === categoria))
      .filter((moduleRow) => (
        !query
          ? true
          : moduleRow.label.toLowerCase().includes(query) || moduleRow.id.toLowerCase().includes(query)
      ));
  }, [availableModules, busquedaModulo, categoria]);

  const kpis = useMemo(() => {
    const rolesCount = roles.length;
    const modulesCount = availableModules.length;
    const permsSelected = permissions.filter((permissionRow) => permissionRow.role_name === selectedRole).length;
    return { rolesCount, modulesCount, permsSelected };
  }, [roles, availableModules.length, permissions, selectedRole]);

  if (loading) {
    return (
      <div className="h-screen flex flex-col items-center justify-center bg-[#F8FAFC]">
        <RefreshCcw className="animate-spin mb-2 text-[#51A7FD]" size={20} />
        <p className="text-[8px] font-bold text-slate-400 uppercase tracking-widest italic">
          Sincronizando roles...
        </p>
      </div>
    );
  }

  return (
    <div className="w-full p-3 md:p-4 bg-[#F8FAFC] min-h-screen font-sans text-slate-700">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-6 px-1">
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-xl bg-white border border-slate-200 shadow-sm flex items-center justify-center text-slate-500">
            <FolderKey size={16} />
          </div>
          <div>
            <h1 className="text-lg md:text-xl font-bold text-slate-800 uppercase tracking-tight">
              Roles
            </h1>
            <p className="text-[9px] font-medium text-slate-400 uppercase tracking-widest mt-0.5 italic">
              Plantillas de acceso por modulo
            </p>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 justify-end">
          {isSuperAdmin && (
            <button
              type="button"
              onClick={() => onNavigate?.('super-admin-sesiones')}
              className="bg-white border border-slate-200 text-slate-600 px-4 py-2.5 rounded-xl shadow-sm hover:bg-slate-50 transition-all flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest"
            >
              <History size={16} /> Auditoría de Sesiones
            </button>
          )}

          <button
            type="button"
            onClick={fetchRoleTemplates}
            className="bg-white border border-slate-200 text-slate-600 px-4 py-2.5 rounded-xl shadow-sm hover:bg-slate-50 transition-all flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest"
          >
            <RefreshCcw size={16} /> Sincronizar
          </button>

          <button
            type="button"
            onClick={() => setIsAdding(true)}
            disabled={!canManageUsers}
            className="bg-[#51A7FD] text-white px-6 py-2.5 rounded-xl shadow-md shadow-blue-100 hover:scale-[1.02] active:scale-95 transition-all flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest justify-center disabled:opacity-60 disabled:hover:scale-100"
          >
            <Plus size={16} /> Crear Rol
          </button>
        </div>
      </div>

      {!canManageUsers ? (
        <div className="mb-4 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-[11px] font-bold text-amber-800">
          Esta pantalla queda en modo consulta. Solo un administrador puede cambiar las plantillas base de acceso por rol.
        </div>
      ) : null}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 flex items-center justify-between">
          <div>
            <p className="text-[8px] font-bold text-slate-400 uppercase tracking-widest">Roles</p>
            <p className="text-[14px] font-bold text-slate-800 tracking-tight mt-0.5">
              {kpis.rolesCount}
            </p>
          </div>
          <ShieldCheck size={18} className="text-amber-600" />
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 flex items-center justify-between">
          <div>
            <p className="text-[8px] font-bold text-slate-400 uppercase tracking-widest">Modulos</p>
            <p className="text-[14px] font-bold text-slate-800 tracking-tight mt-0.5">
              {kpis.modulesCount}
            </p>
          </div>
          <Layers size={18} className="text-[#51A7FD]" />
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 flex items-center justify-between">
          <div>
            <p className="text-[8px] font-bold text-slate-400 uppercase tracking-widest">
              Permisos ({selectedRole})
            </p>
            <p className="text-[14px] font-bold text-slate-800 tracking-tight mt-0.5">
              {kpis.permsSelected}
            </p>
          </div>
          <FolderKey size={18} className="text-emerald-600" />
        </div>
      </div>

      <div className="bg-white p-3 rounded-xl shadow-sm border border-slate-200 mb-6 grid grid-cols-1 lg:grid-cols-4 gap-3">
        <div className="space-y-1">
          <label className="text-[8px] font-bold text-slate-400 uppercase tracking-widest ml-1">
            Rol
          </label>
          <select
            value={selectedRole}
            onChange={(event) => setSelectedRole(event.target.value)}
            className="w-full bg-slate-50 border border-slate-100 px-3 py-1.5 rounded-lg text-[10px] font-bold uppercase outline-none focus:border-[#51A7FD] transition-all shadow-inner"
          >
            {roles.map((roleName) => (
              <option key={roleName} value={roleName}>
                {roleName}
              </option>
            ))}
          </select>
        </div>

        <div className="space-y-1">
          <label className="text-[8px] font-bold text-slate-400 uppercase tracking-widest ml-1">
            Categoria
          </label>
          <select
            value={categoria}
            onChange={(event) => setCategoria(event.target.value)}
            className="w-full bg-slate-50 border border-slate-100 px-3 py-1.5 rounded-lg text-[10px] font-bold uppercase outline-none focus:border-[#51A7FD] transition-all shadow-inner"
          >
            {categories.map((categoryName) => (
              <option key={categoryName} value={categoryName}>
                {categoryName}
              </option>
            ))}
          </select>
        </div>

        <div className="space-y-1 lg:col-span-2">
          <label className="text-[8px] font-bold text-slate-400 uppercase tracking-widest ml-1">
            Busqueda de modulo
          </label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300" size={14} />
            <input
              type="text"
              placeholder="Nombre o id del modulo..."
              className="w-full bg-slate-50 border border-slate-100 pl-9 pr-3 py-1.5 rounded-lg text-[10px] font-bold uppercase outline-none focus:border-[#51A7FD] transition-all shadow-inner"
              value={busquedaModulo}
              onChange={(event) => setBusquedaModulo(event.target.value)}
            />
          </div>
        </div>

        {isAdding ? (
          <div className="lg:col-span-4 bg-slate-50 border border-slate-100 rounded-xl p-3 flex flex-col md:flex-row gap-2 md:items-center">
            <div className="flex-1">
              <label className="text-[8px] font-bold text-slate-400 uppercase tracking-widest ml-1">
                Nuevo rol
              </label>
              <input
                autoFocus
                className="w-full bg-white border border-slate-200 px-3 py-2 rounded-lg text-[10px] font-bold uppercase outline-none focus:border-[#51A7FD] transition-all shadow-sm"
                placeholder="nombre-del-rol"
                value={newRoleName}
                onChange={(event) => setNewRoleName(event.target.value)}
                onKeyDown={(event) => event.key === "Enter" && confirmAddRole()}
              />
              <p className="text-[8px] font-bold text-slate-400 uppercase tracking-widest mt-1 ml-1 italic">
                Se guarda cuando le asignas al menos un modulo.
              </p>
            </div>

            <div className="flex gap-2 md:justify-end">
              <button
                type="button"
                onClick={confirmAddRole}
                disabled={!canManageUsers}
                className="bg-[#3FF2AA] text-[#0B2A36] px-4 py-2.5 rounded-xl shadow-sm hover:scale-[1.02] active:scale-95 transition-all flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest disabled:opacity-60 disabled:hover:scale-100"
              >
                <Check size={16} /> Confirmar
              </button>

              <button
                type="button"
                onClick={() => {
                  setIsAdding(false);
                  setNewRoleName("");
                }}
                className="bg-white border border-slate-200 text-slate-600 px-4 py-2.5 rounded-xl shadow-sm hover:bg-slate-50 transition-all flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest"
              >
                <X size={16} /> Cancelar
              </button>
            </div>
          </div>
        ) : null}
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="bg-slate-50/50 border-b border-slate-100 text-[8px] font-bold text-slate-400 uppercase tracking-widest">
                <th className="px-4 py-3">Modulo</th>
                <th className="px-4 py-3">Categoria</th>
                <th className="px-4 py-3 text-right">Estado</th>
              </tr>
            </thead>

            <tbody className="divide-y divide-slate-50">
              {filteredModules.map((moduleRow) => {
                const active = isChecked(moduleRow.id);

                return (
                  <tr key={moduleRow.id} className="hover:bg-blue-50/30 transition-all">
                    <td className="px-4 py-2">
                      <div className="flex flex-col">
                        <span className="text-[11px] font-bold text-slate-700 uppercase tracking-tight">
                          {moduleRow.label}
                        </span>
                        <span className="text-[8px] font-bold text-slate-400 uppercase tracking-widest">
                          {moduleRow.id}
                        </span>
                      </div>
                    </td>

                    <td className="px-4 py-2">
                      <span className="text-[10px] font-bold text-slate-500 uppercase">
                        {moduleRow.category}
                      </span>
                    </td>

                    <td className="px-4 py-2 text-right">
                      <button
                        type="button"
                        onClick={() => togglePermission(moduleRow.id)}
                        disabled={!canManageUsers}
                        className={`inline-flex items-center gap-2 px-3 py-2 rounded-xl border text-[10px] font-bold uppercase tracking-widest transition-all disabled:opacity-60 disabled:cursor-not-allowed ${
                          active
                            ? "bg-emerald-50 border-emerald-100 text-emerald-700 hover:bg-emerald-50/70"
                            : "bg-slate-50 border-slate-100 text-slate-500 hover:bg-slate-100/60"
                        }`}
                        title={active ? "Permiso activo" : "Permiso inactivo"}
                      >
                        {active ? <Unlock size={14} /> : <Lock size={14} />}
                        {active ? "Permitido" : "Bloqueado"}
                      </button>
                    </td>
                  </tr>
                );
              })}

              {filteredModules.length === 0 ? (
                <tr>
                  <td colSpan={3} className="px-6 py-16 text-center">
                    <ShieldCheck className="text-slate-200 mx-auto mb-4" size={44} strokeWidth={1} />
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest italic">
                      No hay modulos para esta busqueda o categoria.
                    </p>
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>

      <div className="h-10" />
    </div>
  );
};

export default RolesConfig;
